// SPDX-License-Identifier: AGPL-3.0-or-later
import {Logger} from '@app/features/platform/utils/AppLogger';
import {getElectronAPI, getNativePlatform, isDesktop, type NativePlatform} from '@app/features/ui/utils/NativeUtils';
import {checkNativePermission, type NativePermissionResult} from '@app/features/voice/utils/NativePermissions';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('NativePermission');
class NativePermission {
	private _initialized = false;
	private _isDesktop = false;
	private _platform: NativePlatform = 'unknown';
	private _waylandSession = false;
	private _inputMonitoringStatus: NativePermissionResult = 'granted';
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		void this.initialize();
	}
	private async initialize(): Promise<void> {
		const desktop = isDesktop();
		const platform = await getNativePlatform();
		let waylandSession = false;
		let inputMonitoringStatus: NativePermissionResult = 'granted';
		if (desktop && platform === 'macos') {
			inputMonitoringStatus = await checkNativePermission('input-monitoring');
		}
		if (desktop && platform === 'linux') {
			try {
				waylandSession = Boolean((await getElectronAPI()?.getDesktopInfo())?.waylandSession);
			} catch (error) {
				logger.warn('Failed to read desktop session type', error);
			}
		}
		logger.debug('Initialized', {
			desktop,
			platform,
			waylandSession,
			inputMonitoringStatus,
		});
		runInAction(() => {
			this._isDesktop = desktop;
			this._platform = platform;
			this._waylandSession = waylandSession;
			this._inputMonitoringStatus = inputMonitoringStatus;
			this._initialized = true;
		});
	}
	get initialized(): boolean {
		return this._initialized;
	}
	get isDesktop(): boolean {
		return this._isDesktop;
	}
	get isMacOS(): boolean {
		return this._platform === 'macos';
	}
	get isNativeMacDesktop(): boolean {
		return this._isDesktop && this._platform === 'macos';
	}
	get isLinuxWaylandDesktop(): boolean {
		return this._isDesktop && this._platform === 'linux' && this._waylandSession;
	}
	get platform(): NativePlatform {
		return this._platform;
	}
	get inputMonitoringStatus(): NativePermissionResult {
		return this._inputMonitoringStatus;
	}
	get isInputMonitoringGranted(): boolean {
		return this._inputMonitoringStatus === 'granted';
	}
	get shouldShowInputMonitoringBanner(): boolean {
		return this._isDesktop && this._platform === 'macos' && this._inputMonitoringStatus !== 'granted';
	}
	async recheckInputMonitoring(): Promise<NativePermissionResult> {
		if (!this._isDesktop || this._platform !== 'macos') {
			return 'granted';
		}
		const status = await checkNativePermission('input-monitoring');
		runInAction(() => {
			this._inputMonitoringStatus = status;
		});
		logger.debug('Rechecked input monitoring', {status});
		return status;
	}
	setInputMonitoringStatus(status: NativePermissionResult): void {
		this._inputMonitoringStatus = status;
	}
}
export default new NativePermission();
